import type { IntakeTemplate } from '../templateTypes';

export const templatePrismeKmiV1: IntakeTemplate = {
  id: 'prisme-kmi-v1',
  name: 'Prisme - KMI',
  description:
    'Questionnaire KMI structuré en 6 piliers. Chaque question utilise une échelle de 0 à 5 (0 = on ne s\'en occupe pas).',
  documentTypeCode: 'PRISME_KMI',
  version: '1.1.0',
  phases: [
  {
    id: "kmi_p1",
    title: "Pilier 1 — Gouvernance, Stratégie & Organisation",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Target",
    questions: [
      {
        id: "kmi_p1_q01",
        label: "Les objectifs d’affaires sont-ils clairement définis et communiqués documentés?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non définis",
          "2 — Partiellement définis",
          "3 — Définis mais peu suivis",
          "4 — Clairs et suivis",
          "5 — Clairs, mesurés et intégrés au TI",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q02",
        label: "Dans quelle mesure la stratégie TI est-elle alignée avec les objectifs d’affaires ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun alignement clair",
          "2 — Alignement ponctuel",
          "3 — Alignement partiel",
          "4 — Alignement structuré",
          "5 — Alignement intégré avec suivi KPI",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q03",
        label: "Avez-vous des indicateurs de performance d'affaires?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun KPI",
          "2 — KPI informels",
          "3 — KPI définis mais peu utilisés",
          "4 — KPI suivis régulièrement",
          "5 — KPI intégrés à la gouvernance",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q04",
        label: "Avez-vous un comité TI structuré ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune structure de gouvernance",
          "2 — Discussions ad hoc",
          "3 — Comité informel",
          "4 — Comité structuré",
          "5 — Gouvernance intégrée avec décisions tracées",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q05",
        label: "Les rôles et responsabilités TI (RACI) sont-ils définis ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non définis",
          "2 — Informels",
          "3 — Partiellement définis",
          "4 — Documentés",
          "5 — Optimisés et appliqués",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q06",
        label: "Avez-vous des politiques TI formelles ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Partielles",
          "3 — Définies mais peu appliquées",
          "4 — Appliquées",
          "5 — Gouvernées et auditées",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q07",
        label: "Avez-vous un budget TI ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — ponctuel",
          "3 — défini et planifié partiellement",
          "4 — défini selon les besoins d’affaires annuel",
          "5 — Optimisé et piloté selon un plan stratégique",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q08",
        label: "Êtes-vous capable de vous assurer que les projets TI sont livrés en temps et selon les budgets?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Échecs fréquents",
          "2 — Retards constants",
          "3 — Livraison variable",
          "4 — Livraison fiable",
          "5 — Livraison optimisée",
        ],
        required: true,
      },
      {
        id: "kmi_p1_q09",
        label: "Les priorités TI sont-elles claires et respectées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune priorisation",
          "2 — Priorités qui changent constamment",
          "3 — Priorisation partielle",
          "4 — Priorisation structurée",
          "5 — Alignée à la stratégie",
        ],
        required: true,
      },
    ],
  },
  {
    id: "kmi_p2",
    title: "Pilier 2 — Architecture, Infrastructure & Cloud",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Server",
    questions: [
      {
        id: "kmi_p2_q01",
        label: "L’inventaire de votre architecture TI est-elle documentée et à jour ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune documentation",
          "2 — Documentation partielle ou obsolète",
          "3 — Documentation existante mais peu utilisée",
          "4 — Documentée et utilisée",
          "5 — Documentée, maintenue et gouvernée",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q02",
        label: "Avez-vous une vue complète de votre infrastructure ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune visibilité",
          "2 — Partielle",
          "3 — Inventaire incomplet",
          "4 — Inventaire structuré",
          "5 — Vue globale dynamique / CMDB",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q03",
        label: "Quel est le niveau de santé actuel de votre infrastructure ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Infrastructure obsolète critique",
          "2 — Plusieurs composantes vieillissantes",
          "3 — Situation mixte",
          "4 — Globalement à jour",
          "5 — Optimisée avec cycle de renouvellement",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q04",
        label: "Comment gérez-vous la capacité et performance ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun suivi",
          "2 — Réactif",
          "3 — Surveillance partielle",
          "4 — Suivi structuré",
          "5 — Monitoring prédictif",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q05",
        label: "Avez-vous un monitoring structuré ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Alertes de base",
          "3 — Outils partiels",
          "4 — Monitoring structuré",
          "5 — Monitoring avec automatisation et corrélation",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q06",
        label: "Avez-vous un plan de modernisation de votre infrastructure?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Approche réactive",
          "3 — Partielle",
          "4 — Planifiée",
          "5 — Roadmap active",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q07",
        label: "Est-ce que votre infrastructure est adéquate pour vos besoins opérationnels actuels?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non optimisé",
          "2 — Choix historiques",
          "3 — Partiellement optimisé",
          "4 — Optimisé",
          "5 — Optimisation continue",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q08",
        label: "Vos sauvegardes sont-elles fiables et testées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune sauvegarde fiable",
          "2 — Sauvegarde non testée",
          "3 — Sauvegarde fonctionnelle",
          "4 — Sauvegarde testée",
          "5 — Sauvegarde immuable avec tests réguliers",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q09",
        label: "Êtes-vous dépendant de ressources clés ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Dépendance critique",
          "2 — Forte dépendance",
          "3 — Dépendance modérée",
          "4 — Faible dépendance",
          "5 — Organisation résiliente",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q10",
        label: "Êtes-vous dépendant de fournisseurs critiques ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Dépendance forte",
          "2 — Peu de contrôle",
          "3 — Gestion partielle",
          "4 — Gestion maîtrisée",
          "5 — Multi-fournisseurs et gouvernance",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q11",
        label: "Les dépendances techniques sont-elles documentées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Inconnues",
          "2 — Partielles",
          "3 — Documentées partiellement",
          "4 — Bien documentées",
          "5 — Cartographie complète",
        ],
        required: true,
      },
      {
        id: "kmi_p2_q12",
        label: "Est-ce que vos actifs TI sont adéquats pour vos besoins opérationnels futures (5ans)?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non optimisé",
          "2 — Choix historiques",
          "3 — Partiellement optimisé",
          "4 — Optimisé",
          "5 — Optimisation continue",
        ],
        required: true,
      },
    ],
  },
  {
    id: "kmi_p3",
    title: "Pilier 3 — Applications, Données & Intégrations",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Database",
    questions: [
      {
        id: "kmi_p3_q01",
        label: "Avez-vous un inventaire documenté et à jour de vos applications ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune visibilité",
          "2 — Liste partielle",
          "3 — Inventaire incomplet",
          "4 — Inventaire structuré",
          "5 — Portfolio gouverné",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q02",
        label: "Les applications critiques sont-elles identifiées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — À l’intuition",
          "3 — Partiellement",
          "4 — Identifiées",
          "5 — Criticité définie et priorisée",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q03",
        label: "Quel est l’état de santé des applications ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Obsolètes ou instables",
          "2 — Plusieurs problématiques",
          "3 — Situation mixte",
          "4 — Globalement stable",
          "5 — Optimisées",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q04",
        label: "Avez-vous un plan de modernisation applicatif ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Approche réactive",
          "3 — Partielle",
          "4 — Planifiée",
          "5 — Roadmap active",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q05",
        label: "Est-ce que vos applications/données sont adéquates pour vos besoins opérationnels actuels?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non optimisé",
          "2 — Choix historiques",
          "3 — Partiellement optimisé",
          "4 — Optimisé",
          "5 — Optimisation continue",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q06",
        label: "Êtes-vous dépendant de fournisseurs applicatifs ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Dépendance critique",
          "2 — Forte dépendance",
          "3 — Moyenne",
          "4 — Maîtrisée",
          "5 — Stratégie multi-fournisseurs",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q07",
        label: "Les données sont-elles facilement accessibles ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Difficiles d’accès",
          "2 — Accès limité",
          "3 — Accès partiel",
          "4 — Bon accès",
          "5 — Accès optimisé",
        ],
        required: true,
      },
      {
        id: "kmi_p3_q08",
        label: "Est-ce que vos applications/données sont adéquates pour vos besoins opérationnels futures (5ans)?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non optimisé",
          "2 — Choix historiques",
          "3 — Partiellement optimisé",
          "4 — Optimisé",
          "5 — Optimisation continue",
        ],
        required: true,
      },
    ],
  },
  {
    id: "kmi_p4",
    title: "Pilier 4 — Services, Opérations & Expérience Utilisateur",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Users",
    questions: [
      {
        id: "kmi_p4_q01",
        label: "Les services TI sont-ils clairs pour les utilisateurs ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Peu clairs",
          "3 — Partiellement",
          "4 — Oui",
          "5 — Très clairs avec communication active",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q02",
        label: "Les utilisateurs accèdent-ils facilement aux services ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun canal structuré",
          "2 — Email ou téléphone seulement",
          "3 — Outil partiel",
          "4 — Portail structuré",
          "5 — Expérience optimisée",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q03",
        label: "Les demandes de solution sont-elles bien gérés ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Chaos",
          "2 — Réactif",
          "3 — Processus partiel",
          "4 — Structuré",
          "5 — Optimisé",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q04",
        label: "Les temps de résolution sont-ils maîtrisés ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Très longs",
          "2 — Variables",
          "3 — Acceptables",
          "4 — Bons",
          "5 — Optimisés",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q05",
        label: "Mesurez-vous la performance du support ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Très peu",
          "3 — Partiellement",
          "4 — KPI suivis",
          "5 — Performance pilotée",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q06",
        label: "Avez-vous un processus de gestion des changements ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Informel",
          "3 — Partiel",
          "4 — Structuré",
          "5 — Gouverné",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q07",
        label: "Mesurez-vous la satisfaction utilisateur ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Occasionnellement",
          "3 — Partiellement",
          "4 — Régulièrement",
          "5 — Expérience pilotée",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q08",
        label: "Les irritants sont-ils identifiés ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Informellement",
          "3 — Partiellement",
          "4 — Identifiés",
          "5 — Priorisés",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q09",
        label: "Comment est l’expérience utilisateur globale ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Mauvaise",
          "2 — Frustrante",
          "3 — Variable",
          "4 — Bonne",
          "5 — Excellente",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q10",
        label: "Quelle est la perception du service TI ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Très mauvaise",
          "2 — Mauvaise",
          "3 — Moyenne",
          "4 — Bonne",
          "5 — Excellente",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q11",
        label: "Vos activités d’affaire sont-elles efficaces?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Très faible",
          "3 — Partielle",
          "4 — Bonne",
          "5 — Optimisée",
        ],
        required: true,
      },
      {
        id: "kmi_p4_q12",
        label: "Vos procédures sont-elles documentées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Incomplet",
          "4 — Documenté",
          "5 — Maintenu et utilisé",
        ],
        required: true,
      },
    ],
  },
  {
    id: "kmi_p5",
    title: "Pilier 5 — Cybersécurité, Risque & Conformité",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Shield",
    questions: [
      {
        id: "kmi_p5_q01",
        label: "Avez-vous une politique de sécurité ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Document partiel",
          "3 — Définie mais peu appliquée",
          "4 — Appliquée",
          "5 — Gouvernée et auditée",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q02",
        label: "La responsabilité sécurité est-elle définie ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun responsable",
          "2 — Informel",
          "3 — Partiellement défini",
          "4 — Responsable identifié",
          "5 — Gouvernance sécurité structurée",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q03",
        label: "Les employés sont-ils formés à la cybersécurité ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune formation",
          "2 — Formation ponctuelle",
          "3 — Formation annuelle",
          "4 — Programme structuré",
          "5 — Culture sécurité intégrée",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q04",
        label: "Évaluez-vous votre posture de sécurité ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Jamais",
          "2 — Occasionnellement",
          "3 — Évaluation partielle",
          "4 — Évaluation régulière",
          "5 — Audit et amélioration continue",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q05",
        label: "Les risques TI sont-ils identifiés ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Informels",
          "3 — Partiels",
          "4 — Identifiés",
          "5 — Registre structuré",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q06",
        label: "Avez-vous un registre des risques ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Obsolète",
          "3 — Partiel",
          "4 — Maintenu",
          "5 — Gouverné",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q07",
        label: "Avez-vous un registre des incidents ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Obsolète",
          "3 — Partiel",
          "4 — Maintenu",
          "5 — Gouverné",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q08",
        label: "Les accès sont-ils sécurisés (MFA) ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun contrôle",
          "2 — Mots de passe seulement",
          "3 — MFA partiel",
          "4 — MFA généralisé",
          "5 — IAM avec gestion JML",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q09",
        label: "Gérez-vous les vulnérabilités ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun suivi",
          "2 — Réactif",
          "3 — Scan ponctuel",
          "4 — Scan régulier",
          "5 — Gestion proactive",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q10",
        label: "Les correctifs sont-ils appliqués ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Processus manuel",
          "4 — Automatisé",
          "5 — Gouverné",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q11",
        label: "Avez-vous une protection endpoint avancée ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Antivirus simple",
          "3 — Protection partielle",
          "4 — EDR en place",
          "5 — EDR avec SOC",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q12",
        label: "Surveillez-vous les activités suspectes ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Logs non analysés",
          "3 — Surveillance partielle",
          "4 — Surveillance active",
          "5 — SIEM / SOC",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q13",
        label: "Pouvez-vous détecter rapidement un incident ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Très lentement",
          "3 — Variable",
          "4 — Rapidement",
          "5 — Détection avancée",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q14",
        label: "Avez-vous un plan de réponse aux incidents ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Informel",
          "3 — Documenté",
          "4 — Testé",
          "5 — Testé et amélioré",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q15",
        label: "Les données sensibles sont-elles protégées ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Protection de base",
          "4 — Protection adéquate",
          "5 — Gouvernance complète",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q16",
        label: "Avez-vous une traçabilité des actions ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Logs limités",
          "3 — Partielle",
          "4 — Traçabilité",
          "5 — Audit complet",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q17",
        label: "Avez-vous des enjeux de conformité?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Identifiés",
          "4 — Priorisés",
          "5 — Pilotés",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q18",
        label: "Avez-vous bien identifié les actifs TI existants qui requiert des actions en cybersécurité?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Identifiés",
          "4 — Priorisés",
          "5 — Pilotés",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q19",
        label: "Avez-vous déterminé et appliqué les mesures de protection adéquates pour sécuriser vos actifs TI et mettre à l'abri les valeurs et la réputation de l'entreprise?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Identifiés",
          "4 — Priorisés",
          "5 — Pilotés",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q20",
        label: "Avez-vous déterminé et appliqué les mesures de détection adéquates pour notifier le plus rapidement possible les menaces et intrusions?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Identifiés",
          "4 — Priorisés",
          "5 — Pilotés",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q21",
        label: "Avez-vous déterminé et appliqué les mesures de réponse adéquates pour mettre fin et controler les effets néfastes de l'incident?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Identifiés",
          "4 — Priorisés",
          "5 — Pilotés",
        ],
        required: true,
      },
      {
        id: "kmi_p5_q22",
        label: "Avez-vous déterminé et appliqué les mesures de récupération adéquates pour corriger les effets néfastes et remettre l'environnement TI dans un état opérationnel et permettre la reprise des activités d'affaire?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Mixte",
          "4 — Bien en place",
          "5 — Optimisé",
        ],
        required: true,
      },
    ],
  },
  {
    id: "kmi_p6",
    title: "Pilier 6 — Transformation & IA",
    description: "Répondez aux questions de ce pilier (échelle 0 à 5).",
    icon: "Sparkles",
    questions: [
      {
        id: "kmi_p6_q01",
        label: "Avez-vous une vision de transformation numérique ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucune",
          "2 — Idées non structurées",
          "3 — Vision partielle",
          "4 — Vision définie",
          "5 — Vision alignée et pilotée",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q02",
        label: "La transformation est-elle alignée aux affaires ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Variable",
          "4 — Alignée",
          "5 — Pilotée par la valeur",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q03",
        label: "Utilisez-vous l’IA ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun usage",
          "2 — Exploration",
          "3 — Cas ponctuels",
          "4 — Usage structuré",
          "5 — IA intégrée au business",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q04",
        label: "Avez-vous identifié des cas d’usage IA ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Des idées seulement",
          "3 — Partiellement",
          "4 — Identifiés",
          "5 — Priorisés",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q05",
        label: "Avez-vous automatisé certains processus ?",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Aucun",
          "2 — Très peu",
          "3 — Partiellement",
          "4 — Automatisé",
          "5 — Optimisé",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q06",
        label: "Autres",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Mixte",
          "4 — Bien en place",
          "5 — Optimisé",
        ],
        required: true,
      },
      {
        id: "kmi_p6_q07",
        label: "(à définir selon les besoins du client)",
        type: 'select' as const,
        options: [
          "0 — On ne s'en occupe pas",
          "1 — Non",
          "2 — Partiellement",
          "3 — Mixte",
          "4 — Bien en place",
          "5 — Optimisé",
        ],
        required: true,
      },
    ],
  },
  ],
};
